import { useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { Capacitor } from "@capacitor/core"
import { api } from "@/lib/api"
import { useAuthStore } from "@/stores/authStore"
import { scheduleReminders, buildReminderItems } from "@/lib/notifications"

/**
 * Schedules local notifications on native platform.
 * Call this once when the user is authenticated (e.g. in AppLayout or DashboardPage).
 * Runs only on Android/iOS — no-op on web.
 */
export function useScheduleNotifications() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const { data: leasesData } = useQuery({
    queryKey: ["leases", "notifications"],
    queryFn: async () => {
      const res = await api.get("/api/leases?limit=200")
      return res.data.data ?? []
    },
    enabled: isAuthenticated && Capacitor.isNativePlatform(),
    staleTime: 1000 * 60 * 60, // 1 hour
  })

  const { data: rentData } = useQuery({
    queryKey: ["rent-records", "notifications"],
    queryFn: async () => {
      const res = await api.get("/api/rent-records?limit=200")
      return res.data.data ?? []
    },
    enabled: isAuthenticated && Capacitor.isNativePlatform(),
    staleTime: 1000 * 60 * 60,
  })

  const { data: maintenanceData } = useQuery({
    queryKey: ["maintenance", "notifications"],
    queryFn: async () => {
      const res = await api.get("/api/maintenance?limit=200")
      return res.data.data ?? []
    },
    enabled: isAuthenticated && Capacitor.isNativePlatform(),
    staleTime: 1000 * 60 * 60,
  })

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    if (!leasesData || !rentData || !maintenanceData) return

    const items = buildReminderItems({
      leases: leasesData,
      rentRecords: rentData,
      maintenanceRequests: maintenanceData,
    })

    scheduleReminders(items).catch(() => {
      // Silently ignore permission denials or scheduling errors
    })
  }, [leasesData, rentData, maintenanceData])
}
